import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  ViewChild,
  OnDestroy,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { HttpErrorResponse } from '@angular/common/http';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  mergeMap,
  Observable,
  of,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';
import cloneDeep from 'lodash.clonedeep';

import { DprActivitiesGridComponent } from '@dpr-details/components';
import { DprActivityGridData } from '@dpr-details/models/grid';
import { DprActivity, DprDetails, DprDetailsSync } from '@shared/models/dpr';
import { GridSync } from '@grid/models';
import { DprDetailsSyncModelBuilderService } from '@dpr-details/services/sync';
import {
  DprActivityHistoryService,
  DprDetailsService,
} from '@dpr-details/services';
import {
  DprActivitiesGridSyncResult,
  WriteDprActivity,
  WriteDprParallelActivity,
} from '@dpr-details/models/sync';
import {
  DateFilter,
  SelectedSubproject,
  UserPreferences,
} from '@shared/models';
import { ActivityStream } from '@shared/models/project-setup';
import { DprDetailsGridContainerBaseComponent } from '@dpr-details/models/component';
import { SyncService } from '@shared/services/grid';
import { Sync } from '@shared/models/grid';
import { DprAttachmentService } from '@shared/services/dpr';
import { DprService } from '@dpr/services';
import { DprActivityCopyOption } from '@dpr-details/models';
import { Rights } from '@shared/enums';
import { ScreenSizeService } from '@shared/services';

@UntilDestroy()
@Component({
  selector: 'app-dpr-activities-grid-container',
  templateUrl: './dpr-activities-grid-container.component.html',
  styleUrls: ['./dpr-activities-grid-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DprActivitiesGridContainerComponent
  extends DprDetailsGridContainerBaseComponent<DprActivityGridData>
  implements OnInit, OnDestroy
{
  @ViewChild(DprActivitiesGridComponent)
  private gridComponent: DprActivitiesGridComponent;

  gridData$ = new BehaviorSubject<DprActivityGridData[]>(null);
  gridsReady$ = new BehaviorSubject<boolean>(false);
  activityStream$ = new BehaviorSubject<ActivityStream>(null);
  availableDates$ = new BehaviorSubject<DateFilter>(null);
  userPreferences$ = new BehaviorSubject<UserPreferences>(null);

  gridsData = new Map<number, DprActivityGridData[]>();
  gridsChanges = new Map<number, GridSync<DprActivityGridData>>();
  activityStreamId: number;
  isSaving: boolean;

  constructor(
    protected activatedRoute: ActivatedRoute,
    protected screenSizeService: ScreenSizeService,
    private dprDetailsService: DprDetailsService,
    private dprActivityHistoryService: DprActivityHistoryService,
    private dprDetailsSyncModelBuilderService: DprDetailsSyncModelBuilderService,
    private syncService: SyncService,
    private dprAttachmentService: DprAttachmentService,
    private dprService: DprService
  ) {
    super(activatedRoute);

    this.dprDetailsRouteParams = this.activatedRoute.parent.parent.params;
  }

  ngOnInit(): void {
    super.ngOnInit();

    combineLatest([this.selectedSubproject$, this.activatedRoute.params])
      .pipe(
        untilDestroyed(this),
        map(([selectedSubproject, params]) => {
          const activityStream = selectedSubproject.data.activityStreams.find(
            (x) => x.id === +params.activityStreamId
          );

          if (activityStream) {
            this.activityStream$.next(activityStream);
          }
        })
      )
      .subscribe();

    combineLatest([
      this.selectedSubproject$,
      this.dprDetails$.pipe(
        mergeMap((result) =>
          of({
            ...result,
            dprParallelActivities: result.dprParallelActivities.map((pa) =>
              this.mapDprParallelActivityData(pa)
            ),
          })
        )
      ),
      this.dprDetailsOfflineChanges$,
    ])
      .pipe(
        tap(([selectedSubproject, dprDetails, dprDetailsOfflineChanges]) => {
          // timeout is needed to execute this after the grid changes are cleared following a successful save
          setTimeout(() => {
            this.buildGridsData(dprDetails, selectedSubproject);
            this.buildGridsChanges(
              dprDetailsOfflineChanges,
              selectedSubproject
            );
            this.gridsReady$.next(true);
          });
        })
      )
      .subscribe();

    combineLatest([
      this.gridsReady$,
      this.activityStream$,
      this.dprDetailsOfflineChanges$,
    ])
      .pipe(
        untilDestroyed(this),
        filter(
          ([gridReady, activityStream]) => !!gridReady && !!activityStream
        ),
        tap(([_, activityStream]) => {
          // timeout is needed here to allow the grid API to register before setting the predefined changes
          setTimeout(() => {
            this.onActivityStreamChanged(
              this.activityStreamId,
              activityStream.id
            );
            this.activityStreamId = activityStream.id;
          });
        })
      )
      .subscribe();

    combineLatest([this.isAppOnline$, this.selectedSubproject$])
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        filter(
          ([isAppOnline, selectedSubproject]) =>
            !!isAppOnline &&
            selectedSubproject.dprScreen === Rights.Edit &&
            !selectedSubproject.isLocked
        ),
        switchMap(() => this.dprService.getUnlockedDprDates()),
        tap((dates) => this.availableDates$.next(dates))
      )
      .subscribe();

    this.userDetailsFacade.query.userDetails$
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        filter((userSession) => !!userSession),
        tap((userSession) =>
          this.userPreferences$.next(userSession.userPreferences)
        )
      )
      .subscribe();
  }

  autoSave(): void {
    if (this.gridComponent.isDialogModeEditOpen) {
      this.gridComponent.saveDialogDataAndClose();
    }

    super.autoSave();
  }

  autoSaveFromAdminChange(): Observable<boolean> {
    if (this.gridComponent.isDialogModeEditOpen) {
      this.gridComponent.saveDialogDataAndClose();
    }

    if (this.gridComponent.isImportActivitiesDialogOpen) {
      this.gridComponent.closeImportActivitiesDialog();
    }

    return super.autoSaveFromAdminChange();
  }

  save(
    _?,
    isAutoSync = false,
    isAutoSyncFromAdminChange = false
  ): Observable<boolean> {
    if (!this.isDirty$.value || this.isSaving) {
      throw new Error(
        'Unable to save data. There are either no changes to be saved or there is already a save in progress'
      );
    }

    this.isSaving = true;

    this.storeGridState(this.activityStreamId);

    const validateChanges$ =
      isAutoSync || isAutoSyncFromAdminChange
        ? of(true)
        : this.validateChanges(this.gridsChanges);

    const saveChanges$ = validateChanges$.pipe(
      switchMap((valid) => {
        if (!valid) {
          return of(false);
        }

        return this.saveChanges(this.gridsChanges, isAutoSyncFromAdminChange);
      }),
      finalize(() => {
        this.isSaving = false;
      }),
      shareReplay()
    );

    saveChanges$.pipe(untilDestroyed(this)).subscribe();

    return saveChanges$.pipe(map((result) => !!result));
  }

  onSelectedActivitiesCopied(result: DprActivityCopyOption): void {
    this.dprDetailsService
      .copySelectedActivities(result)
      .pipe(
        tap((response) => {
          this.offlineFacade.updateExistingDprDetails(response);
        })
      )
      .subscribe();

    this.dataRefreshService.setRefreshNeeded();
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected getData(): void {}

  protected refreshData(): void {
    this.isDirty$.next(false);
    this.gridsChanges.clear();
    this.gridComponent.reset();
  }

  //#region Private methods

  private validateChanges(
    changes: Map<number, GridSync<DprActivityGridData>>
  ): Observable<boolean> {
    for (const key of changes.keys()) {
      if (!this.gridComponent.validateChanges(changes.get(key))) {
        return of(false);
      }
    }

    return of(true);
  }

  private saveChanges(
    changesMap: Map<number, GridSync<DprActivityGridData>>,
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprActivitiesGridSyncResult> {
    const syncData = this.dprDetailsSyncModelBuilderService.forDprActivities(
      this.selectedSubproject.id,
      this.dprDetails.id,
      this.selectedSubproject.data.activityStreams,
      changesMap
    );

    const saveChanges$ = this.dprDetailsService
      .syncDprActivities(syncData, isAutoSyncFromAdminChange)
      .pipe(shareReplay());

    saveChanges$.pipe(untilDestroyed(this)).subscribe({
      next: (result) => {
        this.onSaveSuccess(result);
      },
      error: (error: HttpErrorResponse) => {
        this.saveErrorHandlerService.onSaveError(
          this.customTabNames.activitiesTabName,
          this.selectedSubproject.id,
          error
        );
      },
    });

    return saveChanges$;
  }

  private onSaveSuccess(result: DprActivitiesGridSyncResult): void {
    this.dprActivityHistoryService.updateFutureDprActivitiesHistories(
      cloneDeep(this.gridsData),
      this.dprDetails
    );

    this.offlineFacade.setDprActivities(
      result.dprActivities,
      result.dprParallelActivities,
      result.firstWarning,
      result.warningStatusTab,
      this.dprDetails.id
    );

    this.refreshData();
  }

  /**
   * @description Separates activities into dedicated sets of data based on activity streams
   */
  private buildGridsData(
    dprDetails: DprDetails,
    selectedSubproject: SelectedSubproject
  ): void {
    const activityStreams = selectedSubproject.data.activityStreams;

    for (const activityStream of activityStreams) {
      let activities: DprActivity[] = [];

      if (activityStream.parallelActivityType === 0) {
        activities = dprDetails.dprActivities;
      } else {
        activities = dprDetails.dprParallelActivities.filter(
          (x) => x.parallelActivityType === activityStream.parallelActivityType
        );
      }

      const gridsData: DprActivityGridData[] = cloneDeep(activities);
      for (const data of gridsData) {
        data.dprActivityAttachments = this.dprAttachmentService.mapToGridData(
          data.dprActivityAttachments
        );
      }

      this.gridsData.set(activityStream.id, gridsData);
    }
  }

  /**
   * @description Initializes the grid changes for all activity streams with the changes saved in offline
   */
  private buildGridsChanges(
    dprDetailsOfflineChanges: DprDetailsSync,
    selectedSubproject: SelectedSubproject
  ): void {
    const activityStreams = selectedSubproject.data.activityStreams;

    for (const activityStream of activityStreams) {
      const offlineChanges = this.getOfflineChanges(
        activityStream.parallelActivityType,
        dprDetailsOfflineChanges
      );

      const offlineChangesCopy = cloneDeep(offlineChanges) as GridSync<
        WriteDprActivity | WriteDprParallelActivity
      >;
      this.gridsChanges.set(
        activityStream.id,
        this.mapOfflineChangesToGridData(
          activityStream.parallelActivityType,
          offlineChangesCopy
        )
      );

      const gridData = this.gridsData.get(activityStream.id) ?? [];
      for (const data of gridData) {
        this.setNestedPredefinedChanges(data, offlineChangesCopy);
      }
    }
  }

  /**
   * @description Sets the offline changes as predefined changes on the data item.
   */
  private setNestedPredefinedChanges(
    data: DprActivityGridData,
    offlineChanges: GridSync<WriteDprActivity | WriteDprParallelActivity>
  ): void {
    const offlineDataChanges = this.getOfflineChangesById(
      data.id,
      offlineChanges
    );
    const isAdded = offlineDataChanges?.addedList?.length;
    const isUpdated = offlineDataChanges?.updatedList?.length;

    const changedItem = isAdded
      ? offlineDataChanges?.addedList[0]
      : isUpdated
      ? offlineDataChanges?.updatedList[0]
      : null;

    if (!changedItem) {
      return;
    }
    if (!changedItem.parallelActivityType) {
      const curItem = changedItem as WriteDprActivity;
      data.syncDprSubActivities = curItem.syncDprSubActivities;
      data.syncDprActivityCustomDataList =
        curItem.syncDprActivityCustomDataList;
      data.syncDprActivityAttachments =
        this.dprAttachmentService.mapSyncDprAttachmentToGridData(
          curItem.syncDprActivityAttachments
        );
    } else {
      const curItem = changedItem as WriteDprParallelActivity;
      data.syncDprSubActivities = curItem.syncDprParallelSubActivities;
      data.syncDprActivityCustomDataList =
        curItem.syncDprParallelActivityCustomDataList;
      data.syncDprActivityAttachments =
        this.dprAttachmentService.mapSyncDprAttachmentToGridData(
          curItem.syncDprParallelActivityAttachments
        );
    }
  }

  /**
   * @description Retrieves the offline changes related to an entity by id.
   */
  private getOfflineChangesById(
    id: number | string,
    offlineChanges: GridSync<WriteDprActivity | WriteDprParallelActivity>
  ): GridSync<WriteDprActivity | WriteDprParallelActivity> {
    return {
      addedList: offlineChanges?.addedList?.filter((x) => x.id === id) ?? [],
      updatedList:
        offlineChanges?.updatedList?.filter((x) => x.id === id) ?? [],
      deletedIds: offlineChanges?.deletedIds ?? [],
    };
  }

  /**
   * @description Changes the state of the current grid by updating the data and the predefined changes
   */
  private onActivityStreamChanged(
    previousValue: number,
    currentValue: number
  ): void {
    if (previousValue == null || currentValue === previousValue) {
      // update grid with the newly selected activity type data
      this.gridData$.next(this.gridsData.get(currentValue));

      // set predefined changes for newly selected activity type data (needed in case we apply offline changes)
      this.gridApi?.setPredefinedChanges(this.gridsChanges.get(currentValue));
      return;
    }

    // store changes of current activity type grid
    this.storeGridState(previousValue);

    // set grid as pristine
    this.gridApi?.reset();

    // initialize the grid according to the selected activity stream
    this.gridComponent.initializeGrid();

    // update grid with the newly selected activity type data
    if (this.gridsData.get(currentValue)) {
      this.gridData$.next(this.gridsData.get(currentValue));
    }

    // set predefined changes for newly selected activity type data
    if (this.gridsChanges.get(currentValue)) {
      this.gridApi?.setPredefinedChanges(this.gridsChanges.get(currentValue));
    }
  }

  /**
   * @description Stores the state of the current grid.
   */
  private storeGridState(activityStreamId: number): void {
    this.gridsChanges.set(activityStreamId, this.gridApi?.getChanges());
    this.gridsData.set(activityStreamId, this.gridComponent.gridData);
  }

  /**
   * @description Retrieves the offline changes for the activity stream specified as parameter.
   */
  private getOfflineChanges(
    parallelActivityType: number,
    dprDetailsOfflineChanges: DprDetailsSync
  ): GridSync<WriteDprActivity | WriteDprParallelActivity> {
    return parallelActivityType
      ? {
          addedList: (
            dprDetailsOfflineChanges?.dprParallelActivities?.addedList ?? []
          ).filter((x) => x.parallelActivityType === parallelActivityType),
          updatedList: (
            dprDetailsOfflineChanges?.dprParallelActivities?.updatedList ?? []
          ).filter((x) => x.parallelActivityType === parallelActivityType),
          deletedIds:
            dprDetailsOfflineChanges?.dprParallelActivities?.deletedIds ?? [],
        }
      : dprDetailsOfflineChanges?.dprActivities ??
          this.syncService.getDefaultSyncModel();
  }

  /**
   * @description Maps parallel activity data values to default ones.
   */
  private mapDprParallelActivityData(
    dprParallelActivity: DprActivity
  ): DprActivity {
    if (!dprParallelActivity) {
      return dprParallelActivity;
    }
    return {
      ...dprParallelActivity,
      dprSubActivities: dprParallelActivity.dprParallelSubActivityList.map(
        (x) => ({ ...x, dprActivityId: x.dprParallelActivityId })
      ),
      dprActivityCustomDataList:
        dprParallelActivity.dprParallelActivityCustomDataList,
      dprActivityAttachments:
        dprParallelActivity.dprParallelActivityAttachments,
    };
  }

  /**
   * @description Maps DPR activity offline changes to grid data sync object.
   */
  private mapOfflineChangesToGridData(
    parallelActivityType: number,
    offlineChanges: Sync<WriteDprActivity | WriteDprParallelActivity>
  ): GridSync<DprActivityGridData> {
    return parallelActivityType
      ? {
          addedList: offlineChanges.addedList.map(
            this.mapWriteDprParallelActivity.bind(this)
          ),
          updatedList: offlineChanges.updatedList.map(
            this.mapWriteDprParallelActivity.bind(this)
          ),
          deletedIds: offlineChanges.deletedIds,
        }
      : {
          addedList: offlineChanges.addedList.map(
            this.mapWriteDprActivity.bind(this)
          ),
          updatedList: offlineChanges.updatedList.map(
            this.mapWriteDprActivity.bind(this)
          ),
          deletedIds: offlineChanges.deletedIds,
        };
  }

  /**
   * @description Maps write DPR activity to grid data object, transforming the DPR attachments.
   */
  private mapWriteDprActivity(x: WriteDprActivity): DprActivityGridData {
    return {
      ...x,
      syncDprActivityAttachments:
        this.dprAttachmentService.mapSyncDprAttachmentToGridData(
          x.syncDprActivityAttachments
        ),
    };
  }

  /**
   * @description Maps write DPR parallel activity to grid data object, transforming the DPR attachments.
   */
  private mapWriteDprParallelActivity(
    x: WriteDprParallelActivity
  ): DprActivityGridData {
    return {
      ...x,
      syncDprSubActivities: x.syncDprParallelSubActivities,
      syncDprActivityCustomDataList: x.syncDprParallelActivityCustomDataList,
      syncDprActivityAttachments:
        this.dprAttachmentService.mapSyncDprAttachmentToGridData(
          x.syncDprParallelActivityAttachments
        ),
    };
  }

  //#endregion
}
